/**
 * tio-api plan eligibility conditions (mirrors Plan::ELIGIBILITY_CONDITIONS)
 * that indicate an SLR loan-standing verification requirement. A plan carrying
 * any of these requires the employee to periodically prove their loans are in
 * good standing.
 */
export const SLR_LOAN_STANDING_ELIGIBILITY_CONDITIONS = [
  'require_document',
  'require_recent_document',
  'require_audit_window_upload',
  'allow_late_requalification',
  'require_name_match',
  'require_required_observations',
  'require_good_standing_status',
  'reject_parent_plus_only',
  // Status-standing rejections (tio-api #10547 seeded their catalog rows) —
  // a plan carrying only these still runs loan-standing verification, so
  // they must count toward planRequiresLoanStandingVerification.
  'reject_all_defaulted',
  'reject_all_delinquent',
  'reject_all_in_collections',
  'reject_past_due',
] as const;

const KNOWN_CONDITIONS = new Set<string>(SLR_LOAN_STANDING_ELIGIBILITY_CONDITIONS);

/**
 * Whether a plan requires SLR loan-standing verification, derived from its
 * `eligibility_conditions`. This is the source of truth for the feature being
 * "enabled" for a plan — tio-api does not send a dedicated `featureEnabled`
 * flag. Wiring this into the dashboard alert gating (replacing the imagined
 * `state.featureEnabled`) is tracked in tio-front-end#8714.
 */
export function planRequiresLoanStandingVerification(
  eligibilityConditions?: readonly string[] | null
): boolean {
  if (!eligibilityConditions) return false;
  return eligibilityConditions.some((condition) => KNOWN_CONDITIONS.has(condition));
}

/**
 * The upload checklist on the verification card: exactly the designed base
 * three (Figma 767-18351/767-18425 — recent date / your name / loan details),
 * for every plan. QA's 8/1 ruling removed the fourth "Current Status" line
 * that used to render when a plan enforced a status-verdict condition — the
 * "Please note: we will run a quick document check…" note already covers the
 * eligibility checks without asserting per-plan enforcement details.
 */
const LOAN_STANDING_GUIDELINES = [
  {
    condition: 'require_recent_document',
    labelKey: 'slr.loan_standing.attention.check_recent_label',
    textKey: 'slr.loan_standing.attention.check_recent',
  },
  {
    condition: 'require_name_match',
    labelKey: 'slr.loan_standing.attention.check_name_label',
    textKey: 'slr.loan_standing.attention.check_name',
  },
  {
    condition: 'require_required_observations',
    labelKey: 'slr.loan_standing.attention.check_details_label',
    textKey: 'slr.loan_standing.attention.check_details',
  },
] as const;

export type LoanStandingGuideline = (typeof LOAN_STANDING_GUIDELINES)[number];

/**
 * The signature keeps the conditions parameter so call sites stay stable,
 * but the list no longer varies by plan (QA 8/1: the checklist is the fixed
 * designed copy; per-plan variance produced lists the designs never showed).
 */
export function loanStandingGuidelines(
  _eligibilityConditions?: readonly string[] | null
): readonly LoanStandingGuideline[] {
  return LOAN_STANDING_GUIDELINES;
}
